// Phase 35c — Tool-call card.
//
// One card per executed tool. Collapsed by default (status + name +
// duration), expandable to show args + result JSON. While the tool is
// still running, shows a spinner. Lives inline in the assistant message
// bubble between paragraphs of streamed text.

import { useState } from "react";
import {
  ChevronDown, ChevronRight, Wrench, CheckCircle2, AlertTriangle,
  Loader2,
} from "lucide-react";

const TOOL_LABEL = {
  calculate_metrics:    "Recompute deal metrics",
  get_market_context:   "Pull live market data",
  run_sensitivity:      "Run sensitivity grid",
  compare_deals:        "Compare deals side-by-side",
  find_similar_deals:   "Find similar deals in portfolio",
  query_portfolio:      "Query portfolio",
};

function _slug(s) {
  return String(s || "").replace(/[^a-z0-9]+/gi, "-").toLowerCase();
}

export default function ToolCard({ name, args, result, durationMs, iteration }) {
  const [open, setOpen] = useState(false);
  const isRunning = result === undefined;
  const isError = result && typeof result === "object" && "error" in result;
  const label = TOOL_LABEL[name] || name;
  const slug = _slug(name);

  const StatusIcon = isRunning
    ? Loader2
    : isError
      ? AlertTriangle
      : CheckCircle2;
  const statusClass = isRunning
    ? "text-indigo-500 animate-spin"
    : isError
      ? "text-amber-600"
      : "text-emerald-600";

  return (
    <div
      data-testid={`tool-card-${slug}`}
      className="my-2 overflow-hidden rounded-xl border border-slate-200 bg-slate-50/60 transition-colors"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        data-testid={`tool-card-toggle-${slug}`}
        className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-slate-100"
      >
        {open ? (
          <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 text-slate-400" />
        )}
        <Wrench className="h-3.5 w-3.5 text-indigo-500" />
        <span className="flex-1 truncate text-xs font-medium text-slate-700">
          {label}
        </span>
        <StatusIcon className={`h-3.5 w-3.5 ${statusClass}`} />
        {typeof iteration === "number" && (
          <span className="rounded bg-slate-200 px-1.5 py-0.5 text-[10px] font-medium text-slate-600">
            #{iteration}
          </span>
        )}
        {typeof durationMs === "number" && (
          <span className="text-[10px] text-slate-400">{durationMs}ms</span>
        )}
      </button>

      {open && (
        <div
          data-testid={`tool-card-body-${slug}`}
          className="border-t border-slate-200 bg-white px-3 py-2"
        >
          <div className="mb-2">
            <p className="mb-1 text-[10px] font-medium uppercase tracking-wide text-slate-400">
              Arguments
            </p>
            <pre className="overflow-x-auto rounded-md bg-slate-900 px-2 py-1.5 text-[11px] text-slate-100">
              {args && Object.keys(args).length > 0
                ? JSON.stringify(args, null, 2)
                : "{}"}
            </pre>
          </div>
          <div>
            <p className="mb-1 text-[10px] font-medium uppercase tracking-wide text-slate-400">
              {isRunning ? "Running…" : isError ? "Error" : "Result"}
            </p>
            {isRunning ? (
              <p className="text-xs italic text-slate-500">Waiting for tool to return…</p>
            ) : (
              <pre className="max-h-48 overflow-auto rounded-md bg-slate-900 px-2 py-1.5 text-[11px] text-slate-100">
                {JSON.stringify(result, null, 2)}
              </pre>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
